import _ from "lodash"
import { useMutation, gql } from "@apollo/client"
import { useParams } from "react-router-dom"
import { useRef, useState, useContext, useEffect } from "react"
import { v4 as uuidv4 } from "uuid"

// Firebase
import { FirebaseContext } from "../../firebase"

// PR
import { Toast } from "primereact/toast"
import { ProgressBar } from "primereact/progressbar"
import { Button } from "primereact/button"
import { confirmPopup } from "primereact/confirmpopup"

// comps
import { LoaderSmall } from "../../components/Loaders"

// context
import { useFormikContext } from "formik"
import { FormContext } from "../../context/formContext"
import { UserContext } from "../../context/userContext"

// styles/helpers
import styles from "../../styles/Form.module.css"
import fieldVisibility from "../../helpers/fieldVisibility"
import fileUploadStyles from "../../styles/TypeFileUnified.module.css"
import { useFirebaseFileUrl } from "../../hooks/useFirebaseFileUrl"

// NOTE: MULTI-FILE SUPPORT REMOVED DUE TO LACK OF SUPPORT IN UPSTREAM COMPONENTS

export default function TypeFile({ field, block, index }) {
  const [progress, setProgress] = useState(0)
  const [fileUrl, setFileUrl] = useState(null)
  const [loading, setLoading] = useState(true)
  const [timestampPrefix] = useState(Date.now())
  const [showProgress, setShowProgress] = useState(false)
  const hideProgressTimeoutRef = useRef(null)
  const nameMapRef = useRef(new Map()) // map original filename -> final filename (this session)
  const pathMapRef = useRef(new Map()) // map final filename -> fullPath (this session)
  const userCtx = useContext(UserContext)
  const firebase = useContext(FirebaseContext)
  const form = useContext(FormContext)
  const isDisabled = !!form?.blocksSaved[block] || !!field?.read_only

  const {
    getStorage,
    ref,
    uploadBytesResumable,
    deleteObject,
    // getDownloadURL is no longer needed here
  } = firebase.storage

  const { idStamp } = useParams()
  const storage = getStorage()
  const toast = useRef(null)
  const fileUploadRef = useRef(null)
  const [displayedFileName, setDisplayedFileName] = useState("") // UI-only filename for upload area
  const [displayedFileSize, setDisplayedFileSize] = useState(0) // size in bytes for UI
  const [previewSrc, setPreviewSrc] = useState(null) // Object URL for file preview
  const prevPreviewRef = useRef(null)
  // drag state for visual feedback (hooks must remain at top level)
  const [dragOver, setDragOver] = useState(false)
  const dragCounter = useRef(0)
  const isEditMode = !!form?.editMode
  const formik = useFormikContext()
  const FIELD_NAME = field.field_name
  const { url: prefetchedUrl, loading: urlLoading } = useFirebaseFileUrl(formik.values[FIELD_NAME])

  // Field validation (image-style settings)
  const fv = field.field_validation || {}
  const file_is_shared = !!fv.file_is_shared
  const file_extension = fv.file_extension // e.g. "image/*" or "application/pdf"
  const file_firestore_storage = fv.file_firestore_storage // e.g. "uploads" bucket path root
  const file_size = fv.file_size
  const file_is_renameable = fv.file_is_renameable

  // Decide storage path strategy
  // If file_firestore_storage provided, use image-style path, else use document-style path
  const usingValidationStorage = !!file_firestore_storage

  useEffect(() => {
    // Register field as loading in FormContext
    form && form.registerLoadingField(field)

    // Notify FormContext that field is loaded
    form && form.completeLoadingField(field)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const FileIcon = () => (
    <div className={fileUploadStyles.fileIcon}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#9CA3AF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14 2v6h6" stroke="#9CA3AF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  )

  // Document-style user type logic
  const handleUserType = () => {
    switch (FIELD_NAME) {
      case "document_participant_file":
        return {
          userPath: "participant",
          id: userCtx.current_participant_id,
        }
      case "document_staff_file":
        return {
          userPath: "staff",
          id: userCtx.session.user.staff_id,
        }
      default:
        return {
          userPath: "not-set",
          id: "not-set",
        }
    }
  }
  const userType = handleUserType()

  // Build base path
  let FIELD_PATH
  if (usingValidationStorage) {
    // Image-style path
    const orgPart = file_is_shared
      ? ""
      : `/${userCtx.session.user.organization_name}`
    FIELD_PATH = `${file_firestore_storage}${orgPart}`
  } else {
    // Document-style path
    FIELD_PATH = `documents/${userCtx.session.user.enterprise_id}/${userCtx.session.user.organization_id}/${userType.userPath}/${userType.id}`
  }

  // GraphQL mutation for removing reference in block
  const REMOVE_FILE_QUERY = gql`
    mutation RemoveFileFromBlock($idStamp: String!) {
      removeFileRef(idStamp: $idStamp) {
        status
      }
    }
  `

  const [removeFileRefFromBlock] = useMutation(REMOVE_FILE_QUERY, {
    onError: (err) => {
      console.error("[removeFileRef] GraphQL error:", {
        message: err.message,
        graphQLErrors: err.graphQLErrors,
        networkError: err.networkError,
      })
      toast.current?.show({
        severity: "error",
        summary: "Remove reference failed",
        detail: err.message,
        life: 6000,
      })
    },
  })

  useEffect(() => {
    const path = formik.values[FIELD_NAME] || null
    setLoading(urlLoading)

    if (!path || !prefetchedUrl) {
      // Clear UI state if there's no path or URL
      setFileUrl(null)
      setPreviewSrc(null)
      setDisplayedFileName("")
      setDisplayedFileSize(0)
      return
    }

    setFileUrl(prefetchedUrl)

    // If this looks like an image, use the download URL as preview
    const imageExts = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"]
    const ext = (path || "").split(".").pop().toLowerCase()
    if ((file_extension && file_extension.includes("image")) || imageExts.includes(ext)) {
      setPreviewSrc(prefetchedUrl)
    } else {
      setPreviewSrc(null)
    }

    // Show the stored filename in the UI
    try {
      const filename = _.split(path, "/").pop()
      setDisplayedFileName(filename)
      // size not available from downloadURL; leave displayedFileSize as 0
    } catch (err) {
      console.warn("[TypeFile] failed to parse filename from path:", err)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values[FIELD_NAME], prefetchedUrl, urlLoading])

  // clear any pending hide timers on unmount
  useEffect(() => {
    return () => {
      if (hideProgressTimeoutRef.current) clearTimeout(hideProgressTimeoutRef.current)
    }
  }, [])

  const checkIfRequired = () => {
    try {
      const req = fv?.required
      if (!req) return false
      return JSON.parse(req[0])
    } catch (error) {
      console.warn("[checkIfRequired] failed to parse required flag:", error)
      return false
    }
  }
  const isRequired = checkIfRequired()

  // turn an "accept" string like "image/png,image/gif,image/jpeg,image/jpg"
  // into a friendly list like "JPEG, PNG, or GIF".
  // If the input doesn't look like a comma-separated mime/ext list, return it unchanged.
  const humanizeAccept = (accept) => {
    if (!accept || typeof accept !== "string") return accept
    const tokens = accept.split(",").map(s => s.trim()).filter(Boolean)
    if (!tokens.length) return accept

    // tokens must be mime types (contain '/') or start with '.' (extensions); otherwise bail out
    for (const t of tokens) {
      if (!(t.startsWith(".") || t.includes("/"))) return accept
    }

    const mapMimeToLabel = (t) => {
      const lower = t.toLowerCase()
      if (lower.startsWith(".")) return lower.slice(1).toUpperCase()
      if (lower.endsWith("/*")) {
        const type = lower.split("/")[0]
        // e.g. "image/*" -> "Images"
        return type === "image" ? "Images" : `${type.charAt(0).toUpperCase()}${type.slice(1)} files`
      }
      const [type, sub] = lower.split("/")
      if (!sub) return t
      // normalize common image subs
      if (sub.includes("jpeg") || sub.includes("jpg")) return "JPEG"
      if (sub.includes("png")) return "PNG"
      if (sub.includes("gif")) return "GIF"
      if (sub.includes("webp")) return "WEBP"
      if (sub.includes("bmp")) return "BMP"
      if (sub.includes("svg")) return "SVG"
      if (sub.includes("tiff") || sub.includes("tif")) return "TIFF"
      if (type === "application" && sub.includes("pdf")) return "PDF"
      if (type === "text" && sub.includes("plain")) return "TXT"
      // fallback: use subtype uppercased
      return sub.toUpperCase()
    }

    const labels = tokens.map(mapMimeToLabel)
    const unique = Array.from(new Set(labels)) // dedupe

    // preferred order for nicer output (put common types first)
    const preferred = ["JPEG", "PNG", "GIF", "SVG", "WEBP", "BMP", "TIFF", "PDF", "TXT", "Images"]
    unique.sort((a, b) => {
      const ia = preferred.indexOf(a)
      const ib = preferred.indexOf(b)
      if (ia === -1 && ib === -1) return a.localeCompare(b)
      if (ia === -1) return 1
      if (ib === -1) return -1
      return ia - ib
    })

    if (unique.length === 1) return unique[0]
    if (unique.length === 2) return `${unique[0]} or ${unique[1]}`
    return `${unique.slice(0, -1).join(", ")}, or ${unique.slice(-1)[0]}`
  }

  // Validate a File object against an accept string and max bytes.
  // Returns { ok: boolean, reason?: "size" | "type" }
  const validateFile = (file, acceptStr = file_extension || "", maxBytes = typeof file_size === "number" ? file_size : 10000000) => {
    if (!file) return { ok: false, reason: "type" }
    const patterns = (acceptStr || "").split(",").map(s => s.trim()).filter(Boolean)
    const extFromName = (name) => {
      const p = name.split(".")
      return p.length > 1 ? `.${p.pop().toLowerCase()}` : ""
    }
    const matchesPattern = (fileObj, pat) => {
      if (!pat) return true
      if (pat.startsWith(".")) return extFromName(fileObj.name) === pat.toLowerCase()
      if (pat.endsWith("/*")) {
        const typeGroup = (fileObj.type || "").split("/")[0]
        return typeGroup && `${typeGroup}/*` === pat
      }
      if (pat.includes("/")) return (fileObj.type || "").toLowerCase() === pat.toLowerCase()
      return extFromName(fileObj.name) === `.${pat.toLowerCase()}`
    }

    if (file.size > maxBytes) return { ok: false, reason: "size" }
    if (!patterns.length) return { ok: true }
    for (const p of patterns) {
      if (matchesPattern(file, p)) return { ok: true }
    }
    return { ok: false, reason: "type" }
  }

  // unified toast for validation failures (size/type)
  const showValidationToast = ({ reason, allowedBytes, acceptStr } = {}) => {
    if (reason === "size") {
      const mb = (typeof allowedBytes === "number") ? (allowedBytes / 1000000) : ""
      toast.current?.show({
        severity: "warn",
        summary: "File too large",
        detail: `Max size ${mb} MB`,
        life: 4000,
      })
    } else {
      toast.current?.show({
        severity: "warn",
        summary: "Invalid file type",
        detail: `Allowed types: ${humanizeAccept(acceptStr) || acceptStr || "any"}`,
        life: 4000,
      })
    }
  }

  const accept = file_extension || "application/pdf"
  const maxFileSize = typeof file_size === "number" ? file_size : 10000000

  const removeHandler = ({ file, fullPath: passedFullPath } = {}) => {
    // Determine the exact fullPath to delete
    const currentVal = formik.values[FIELD_NAME]
    let targetFullPath = passedFullPath

    // If we didn't get fullPath directly, try to resolve
    if (!targetFullPath) {
      const currStr = typeof currentVal === "string" ? currentVal : ""
      if (isEditMode) {
        if (currStr) {
          // stored value is the path
          targetFullPath = currStr
        } else if (typeof currentVal === "string") {
          targetFullPath = currentVal
        }
      } else {
        // New session removal (from uploader list)
        const mappedFinal = nameMapRef.current.get(file?.name)
        const finalName =
          mappedFinal ??
          (!usingValidationStorage && !file_is_renameable
            ? `${timestampPrefix}_${file?.name}`
            : file?.name)
        // Only attempt to delete if we have a recorded path for this session.
        // If there's no mapping it means the file was never uploaded -> skip delete.
        targetFullPath = pathMapRef.current.get(finalName) || null
      }
    }

    if (!targetFullPath) {
      // Nothing to delete from storage for files that were never uploaded.
      // Resolve so callers (e.g. replace flow) can continue without error.
      return Promise.resolve()
    }

    const fileRef = ref(storage, targetFullPath)

    // Return the promise so callers can await removal (used when replacing files)
    return deleteObject(fileRef)
      .then(async () => {
        // Update form value (string)
        formik.setFieldValue(FIELD_NAME, "")
        setFileUrl(null)
        // clear UI display
        setDisplayedFileName("")
        setDisplayedFileSize(0)
        // revoke any local object URL preview
        try {
          if (prevPreviewRef.current) {
            URL.revokeObjectURL(prevPreviewRef.current)
            prevPreviewRef.current = null
          }
        } catch (err) {
          console.warn("[removeHandler] failed to revoke preview object URL:", err)
        }
        setPreviewSrc(null)

        // mark touched so required/Save updates
        formik.setFieldTouched(FIELD_NAME, true, false)

        try {
          if (!idStamp) {
            console.warn("removeFileRef skipped: missing idStamp route param")
          } else {
            await removeFileRefFromBlock({ variables: { idStamp: String(idStamp) } })
          }
          toast.current.show({
            severity: "info",
            summary: "Success",
            detail: "File deleted and reference removed.",
          })
        } catch (err) {
          console.error("[removeHandler] removeFileRefFromBlock or toast failed:", err)
        }
      })
      .catch((error) => {
        console.error("[storage.deleteObject] error:", error)
        toast.current?.show({
          severity: "error",
          summary: "Delete failed",
          detail: error?.message || "Unable to delete file",
        })
        // propagate so callers can react if needed
        throw error
      })
  }

  const uploadComponent = () => {
    // human friendly size (moved up so read-only branch can use it)
    const formatBytes = (bytes) => {
      if (bytes === 0) return "0 B"
      if (!bytes) return ""
      const sizes = ["B", "KB", "MB", "GB", "TB"]
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      return `${(bytes / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${sizes[i]}`
    }

    const handleDownloadFile = async () => {
      const val = formik.values[FIELD_NAME]
      const currentPath = typeof val === "string" ? val : ""
      const filename = _.split(currentPath, "/").pop()

      const url = fileUrl
      if (!url) return

      let blobUrl
      const link = document.createElement("a")

      try {
        const response = await fetch(url)
        if (!response.ok) {
          throw new Error(`Download failed: ${response.statusText}`)
        }
        const blob = await response.blob()
        blobUrl = URL.createObjectURL(blob)
        link.href = blobUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
      } catch (error) {
        console.error("File download failed:", error)
        toast.current?.show({
          severity: "error",
          summary: "Download Failed",
          detail: "Could not download the file.",
          life: 4000,
        })
      } finally {
        if (link.parentNode) {
          document.body.removeChild(link)
        }
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl)
        }
      }
    }

    const renderDropPanelContent = (readOnly) => {
      if (displayedFileName) {
        return (
          <div className={fileUploadStyles.fileItem}>
            <div className={fileUploadStyles.fileItemInner}>
              {previewSrc ? (
                <img src={previewSrc} alt="preview" className={fileUploadStyles.thumbnail} />
              ) : (
                <div className={fileUploadStyles.fileIcon}>
                  <FileIcon />
                </div>
              )}
              <div>
                <div className={fileUploadStyles.fileName}>{displayedFileName}</div>
                {displayedFileSize ? (
                  <div className={fileUploadStyles.fileSize}>{formatBytes(displayedFileSize)}</div>
                ) : null}
              </div>
            </div>

            <div>
              {fileUrl && (readOnly || isEditMode) && (
                <Button
                  type="button"
                  className="p-button-text"
                  icon="pi pi-download"
                  aria-label="Download file"
                  onClick={handleDownloadFile}
                  style={{ marginRight: 8 }}
                />
              )}

              {/* Delete only available in editable (non-read-only) paths */}
              {!readOnly && (
                <Button
                  type="button"
                  className="p-button-text p-button-danger"
                  icon="pi pi-times"
                  aria-label="Delete file"
                  onClick={(e) => onDeleteClick(e)}
                />
              )}
            </div>
          </div>
        )
      }

      // No file -> render nothing
      return null
    }

    // If disabled (saved block or read-only field), render read-only view: no upload/delete.
    if (isDisabled) {
      return (
        <>
          <div className={fileUploadStyles.container}>
            <div className={fileUploadStyles.headerBar}>
              <div className={fileUploadStyles.headerInner}>
                {/* Read-only: no input or browse button */}
              </div>
            </div>

            <div className={fileUploadStyles.dropPanel}>
              {renderDropPanelContent(true)}
            </div>
          </div>

          {showProgress && <ProgressBar value={Number.isFinite(progress) ? Math.round(progress * 10) / 10 : progress} style={{ margin: "10px 0" }} />}
          {formik.touched[FIELD_NAME] && formik.errors[FIELD_NAME] && (
            <div className={styles.errFieldMsg}>{formik.errors[FIELD_NAME]}</div>
          )}
        </>
      )
    }

    const scheduleHideProgress = () => {
      if (hideProgressTimeoutRef.current) clearTimeout(hideProgressTimeoutRef.current)
      hideProgressTimeoutRef.current = setTimeout(() => {
        setShowProgress(false)
        setProgress(0)
      }, 2000)
    }

    const uploadFile = async (file) => {
      if (!file) return

      // Final safety check before starting upload
      const validation = validateFile(file, accept, maxFileSize)
      if (!validation.ok) {
        if (validation.reason === "size") {
          showValidationToast({ reason: "size", allowedBytes: maxFileSize })
        } else {
          showValidationToast({ reason: "type", acceptStr: file_extension })
        }
        return
      }

      if (hideProgressTimeoutRef.current) clearTimeout(hideProgressTimeoutRef.current)
      setShowProgress(true)
      setProgress(0)

      const totalBytes = file.size || 1

      // Decide final upload filename
      let uploadName
      if (file_is_renameable) {
        // keep extension if present
        const ext = file.name.includes(".") ? `.${file.name.split(".").pop()}` : ""
        uploadName = `${uuidv4()}${ext}`
      } else if (!usingValidationStorage && !isEditMode) {
        uploadName = `${timestampPrefix}_${file.name}`
      } else {
        uploadName = file.name
      }

      const fullPath = `${FIELD_PATH}/${uploadName}`
      const storageRef = ref(storage, fullPath)
      const contentType = file?.type || file_extension || "application/octet-stream"
      const metadata = { contentType }
      const fileToUpload = file_is_renameable ? new File([file], uploadName, { type: file?.type }) : file

      // keep mappings for reliable removal during this session
      nameMapRef.current.set(file.name, uploadName)
      pathMapRef.current.set(uploadName, fullPath)

      try {
        const uploadTask = uploadBytesResumable(storageRef, fileToUpload, metadata)
        let timeoutId

        await new Promise((resolve, reject) => {
          timeoutId = setTimeout(() => {
            reject(new Error('Upload timed out'))
          }, 60000) // Timeout after 60 seconds

          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const pct = (snapshot.bytesTransferred / totalBytes) * 100
              setShowProgress(true)
              setProgress(pct)
            },
            (error) => reject(error),
            () => {
              clearTimeout(timeoutId)
              resolve()
            }
          )
        })

        formik.setFieldValue(FIELD_NAME, fullPath) // Store path as string
        formik.setFieldTouched(FIELD_NAME, true, false) // ensure validation/touched fires (required + Save button)

        // update UI display with the final filename we uploaded
        setDisplayedFileName(uploadName || file.name)
        setDisplayedFileSize(file.size || 0)

        // For image files create an object URL for preview and remember to revoke later.
        try {
          if (file && file.type && file.type.startsWith && file.type.startsWith("image/")) {
            try {
              if (prevPreviewRef.current) URL.revokeObjectURL(prevPreviewRef.current)
            } catch (err) {
              console.warn("[uploadFile] failed to revoke previous preview object URL:", err)
            }
            const obj = URL.createObjectURL(file)
            prevPreviewRef.current = obj
            setPreviewSrc(obj)
          } else {
            // non-image: clear any previous object URL preview (stored preview may remain if stored image)
            try {
              if (prevPreviewRef.current) URL.revokeObjectURL(prevPreviewRef.current)
            } catch (err) {
              console.warn("[uploadFile] failed to revoke previous preview object URL (non-image path):", err)
            }
            prevPreviewRef.current = null
            // do not clobber previewSrc if a stored image URL is appropriate
            if (!file || !(file.type && file.type.startsWith && file.type.startsWith("image/"))) {
              // only clear previewSrc if it was an object URL (we cleared prevPreviewRef)
              // keep stored download preview (fileUrl) as previewSrc if present
              if (!fileUrl) setPreviewSrc(null)
            }
          }
        } catch (err) {
          console.warn("[uploadFile] error handling preview object URL:", err)
        }
      } catch (err) {
        throw err
      } finally {
        scheduleHideProgress()
      }
    }

    const onDrop = async (ev) => {
      ev.preventDefault()
      const files = ev.dataTransfer?.files ? Array.from(ev.dataTransfer.files) : []
      if (files.length > 1) {
        toast.current?.show({
          severity: "warn",
          summary: "Only one file allowed",
          detail: "Please drop a single file.",
          life: 4000,
        })
        return
      }
      if (files.length === 1) {
        const f = files[0]
        const validation = validateFile(f, accept, maxFileSize)
        if (!validation.ok) {
          if (validation.reason === "size") {
            showValidationToast({ reason: "size", allowedBytes: maxFileSize })
          } else {
            showValidationToast({ reason: "type", acceptStr: accept })
          }
          return
        }
        // replace existing if present
        const existingPath = typeof formik.values[FIELD_NAME] === "string" ? formik.values[FIELD_NAME] : ""
        if (existingPath) {
          try { await removeHandler({ fullPath: existingPath }) } catch (err) { console.error("[onDrop] removeHandler failed:", err) }
        }
        await uploadFile(f)
      }
    }

    // drag handlers that toggle the container dragOver class (use counter to avoid flicker)
    const handleDragEnter = (e) => {
      e.preventDefault()
      e.stopPropagation()
      dragCounter.current++
      if (dragCounter.current > 0) setDragOver(true)
    }

    const handleDragOverLocal = (e) => {
      e.preventDefault()
      e.stopPropagation()
      setDragOver(true)
    }

    const handleDragLeave = (e) => {
      e.preventDefault()
      e.stopPropagation()
      dragCounter.current = Math.max(0, dragCounter.current - 1)
      if (dragCounter.current === 0) setDragOver(false)
    }

    const handleDropLocal = async (e) => {
      e.preventDefault()
      e.stopPropagation()
      dragCounter.current = 0
      setDragOver(false)
      await onDrop(e)
    }

    const onInputChange = async (ev) => {
      const files = ev.target?.files ? Array.from(ev.target.files) : []
      if (files.length > 1) {
        toast.current?.show({
          severity: "warn",
          summary: "Only one file allowed",
          detail: "Please select a single file.",
          life: 4000,
        })
        // clear native input
        try { ev.target.value = "" } catch (err) { console.warn("[onInputChange] failed to clear native input value:", err) }
        return
      }
      if (files.length === 1) {
        const f = files[0]
        const validation = validateFile(f, accept, maxFileSize)
        if (!validation.ok) {
          if (validation.reason === "size") {
            showValidationToast({ reason: "size", allowedBytes: maxFileSize })
          } else {
            showValidationToast({ reason: "type", acceptStr: accept })
          }
          try { ev.target.value = "" } catch (err) { console.warn("[onInputChange] failed to clear native input value after validation fail:", err) }
          return
        }
        const existingPath = typeof formik.values[FIELD_NAME] === "string" ? formik.values[FIELD_NAME] : ""
        if (existingPath) {
          try { await removeHandler({ fullPath: existingPath }) } catch (err) { console.error("[onInputChange] removeHandler failed:", err) }
        }
        await uploadFile(f)
      }
    }

    const browseClick = (inputEl) => inputEl && inputEl.click()

    // Delete handler: immediate delete in non-edit, confirmation in edit mode
    const onDeleteClick = (event) => {
      try { event?.preventDefault?.() } catch (err) { console.warn("[onDeleteClick] preventDefault failed:", err) }

      const currentPath = typeof formik.values[FIELD_NAME] === "string" ? formik.values[FIELD_NAME] : ""
      const clearLocal = () => {
        setDisplayedFileName("")
        setDisplayedFileSize(0)
        try {
          if (prevPreviewRef && prevPreviewRef.current) {
            URL.revokeObjectURL(prevPreviewRef.current)
            prevPreviewRef.current = null
          }
        } catch (err) {
          console.warn("[onDeleteClick] failed to revoke preview object URL during clearLocal:", err)
        }
        setPreviewSrc(null)
      }

      if (!isEditMode) {
        // non-edit: remove immediately
        if (currentPath) {
          removeHandler({ fullPath: currentPath }).catch((err) => { console.error("[onDeleteClick] removeHandler failed (non-edit):", err) })
        } else {
          clearLocal()
        }
        return
      }

      // edit mode: require confirmation
      confirmPopup({
        target: event?.currentTarget,
        message: (
          <>
            <div>
              Are you sure you want to remove this file?
            </div>
            <div>
              Deleting the file removes it immediately and requires a new file upload.
            </div>
          </>),
        icon: "pi pi-exclamation-triangle",
        acceptClassName: "p-button-danger",
        accept: () => {
          if (currentPath) {
            removeHandler({ fullPath: currentPath }).catch((err) => { console.error("[onDeleteClick] removeHandler failed (edit accept):", err) })
          } else {
            clearLocal()
          }
        },
        reject: () => {
          /* cancelled */
        },
      })
    }

    return (
      <>
        <div
          className={`${fileUploadStyles.container} ${dragOver ? fileUploadStyles.dragOver : ""}`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOverLocal}
          onDragLeave={handleDragLeave}
          onDrop={handleDropLocal}
        >
          {/* header + browse button */}
          <div className={fileUploadStyles.headerBar}>
            <div className={fileUploadStyles.headerInner}>
              <input
                ref={fileUploadRef}
                type="file"
                name={FIELD_NAME}
                accept={accept}
                style={{ display: "none" }}
                onChange={onInputChange}
              />
              <Button
                type="button"
                label="Browse"
                icon="pi pi-folder-open"
                onClick={() => browseClick(fileUploadRef.current)}
              />
            </div>
          </div>

          {/* drag & drop + display added file */}
          <div className={fileUploadStyles.dropPanel}>
            <div className={styles.description}>Drag and drop files to here to upload.</div>
            {renderDropPanelContent(false)}
          </div>
        </div>

        {showProgress && <ProgressBar value={Number.isFinite(progress) ? Math.round(progress * 10) / 10 : progress} style={{ margin: "10px 0" }} />}
        {formik.touched[FIELD_NAME] && formik.errors[FIELD_NAME] && (
          <div className={styles.errFieldMsg}>{formik.errors[FIELD_NAME]}</div>
        )}
      </>
    )
  }

  return (
    <div
      className={styles.field}
      style={{
        order: index,
        display: fieldVisibility(field.field_visibility),
      }}
    >
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <Toast ref={toast} />
      {isEditMode && loading ? (
        <div className={fileUploadStyles.loaderWrap}>
          <LoaderSmall />
        </div>
      ) : (
        uploadComponent()
      )}
    </div>
  )
}